/**
 * @see https://umijs.org/zh-CN/plugins/plugin-access
 * */
import { PrepayConsumeEnum, type CurrentUserInfo } from '@/typings/user';
/**
 * 使用权限管理
 */
import { ROLE } from '@/pages/PersonnelManageNew/data';
import { BUSINESS_UNIT } from './data/constant';

// 团队角色权限标识常量
const ROLE_PERMISSIONS = {
  /** 主管理员 */
  IS_MANAGER: 'isManager',
  /** 团队用户 */
  IS_TEAM_USER: 'isTeamUser',
  /** 子管理员 */
  IS_CUSTOMER: 'isCustomer',

  /** TeamUser不可访问 */
  IS_TEAM_USER_NOT: 'isNotTeamUser',
  /** Customer不可访问 */
  IS_CUSTOMER_NOT: 'isNotCustomer',
  /** Manager不可访问 */
  IS_MANAGER_NOT: 'isNotManager',

  /** 主管理员或子管理员 */
  IS_MANAGER_OR_CUSTOMER: 'isManagerOrCustomer',
  /** 所有团队角色权限 */
  ALL_VIEW: 'allView',
  /** 团队角色不可访问权限 */
  NOT_VIEW: 'notView',
  /** 单独团队用户可访问，其他角色也可访问 */
  IS_MANAGER_OR_CUSTOMER_OR_TEAM_USER: 'isManagerOrCustomerOrTeamUser',
  /** 团队用户并且未归属团队 */
  IS_TEAM_USER_NOT_BELONG_TEAM: 'isTeamUserNotBelongTeam',
} as const;

export default function access(initialState: { currentUser?: CurrentUserInfo | undefined }) {
  const { currentUser } = initialState || {};
  // 预付实销
  const isPrepayConsume = currentUser?.isPrepayConsume === PrepayConsumeEnum.YES;
  const isNonPrepayConsume = !isPrepayConsume;
  // 非MadHouse
  const notMh = currentUser?.from !== 4;
  // 防盗刷灰度
  const isBrushMonitoring = currentUser?.customerBrushMonitoring;
  // 新兴媒体AI开户灰度
  const isEmergingMediaAiAssist = !!currentUser?.grayscale?.emergingMediaAiAssistEnabled;
  // 封禁池/提交封禁预提 灰度（true 时展示提交封禁预提按钮）
  const isDisabledPool = !!currentUser?.grayscale?.disabledPool;
  // OpenAI开户灰度（开户申请、开户列表、账户管理列表，灰度内才显示）
  const isOpenaiGrayscale = !!currentUser?.grayscale?.openaiEnabled;
  // TT风控大盘灰度（true 时 TikTok 媒体消耗质量 tab 才展示）
  const isTtRiskVlDashboard = !!currentUser?.grayscale?.ttRiskVlDashboardEnabled;
  // 用户组（主子账号逻辑）
  const groupInfo = currentUser?.groupInfo;
  // 团队管理
  const teamManage = currentUser?.grayscale?.teamManage;
  // BV
  const isBV = currentUser?.companyInfo?.signBusinessUnit === BUSINESS_UNIT.BV;
  // TOMATO
  const isTomato = currentUser?.companyInfo?.signBusinessUnit === BUSINESS_UNIT.TOMATO;

  // 主管理员
  const isManager = teamManage && currentUser?.teamInfo?.teamRole === ROLE.MANAGER;
  // 团队用户
  const isTeamUser = teamManage && currentUser?.teamInfo?.teamRole === ROLE.TEAM_USER;
  // 子管理员
  const isCustomer = teamManage && currentUser?.teamInfo?.teamRole === ROLE.CUSTOMER;

  const isTeamUserNotBelongTeam = isTeamUser && !currentUser?.teamInfo?.teamId;

  const rolePermissions = {
    /** 主管理员 */
    [ROLE_PERMISSIONS.IS_MANAGER]: isManager,
    /** 团队用户 */
    [ROLE_PERMISSIONS.IS_TEAM_USER]: isTeamUser,
    /** 子管理员 */
    [ROLE_PERMISSIONS.IS_CUSTOMER]: isCustomer,

    /** TeamUser不可访问 */
    [ROLE_PERMISSIONS.IS_TEAM_USER_NOT]: !isTeamUser,
    /** Customer不可访问 */
    [ROLE_PERMISSIONS.IS_CUSTOMER_NOT]: !isCustomer,
    /** Manager不可访问 */
    [ROLE_PERMISSIONS.IS_MANAGER_NOT]: !isManager,

    /** 主管理员或子管理员 */
    [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER]: isManager || isCustomer,
    /** 所有权限 */
    [ROLE_PERMISSIONS.ALL_VIEW]: true,
    /** 团队角色不可访问权限 */
    [ROLE_PERMISSIONS.NOT_VIEW]: false,
    /** 单独团队用户可访问，其他角色也可访问 */
    [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER_OR_TEAM_USER]: isTeamUser || isCustomer || isManager,
    /** 团队用户并且未归属团队 */
    [ROLE_PERMISSIONS.IS_TEAM_USER_NOT_BELONG_TEAM]: isTeamUserNotBelongTeam,
  };

  // 简化的权限判断函数
  const getAccess = ({
    condition,
    roleRestrictions,
  }: {
    condition: boolean;
    roleRestrictions?: (keyof typeof rolePermissions)[];
  }) => {
    // 如果没有团队管理权限，直接返回条件结果
    if (!teamManage) {
      return condition;
    }

    // 如果有角色限制，检查所有角色权限（需要全部满足）
    if (roleRestrictions && roleRestrictions.length > 0) {
      const hasAllPermissions = roleRestrictions.every(
        (restriction) => rolePermissions[restriction],
      );
      if (!hasAllPermissions) {
        return false;
      }
    }

    return condition;
  };

  // 菜单权限列表
  const menuAccessList = {
    // 概览，团队用户不可访问
    overview: getAccess({ condition: true, roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT] }),
    // FB需求，所有团队 Manager、Customer、TeamUser 可访问
    facebookNeeds: getAccess({
      condition: true,
      roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER_OR_TEAM_USER],
    }),
    // OpenAI账户管理列表：非MadHouse 且 非预付实销 且 团队用户不可见，并且需在OpenAI开户灰度范围内
    openaiNeeds: getAccess({
      condition: notMh && isNonPrepayConsume && isOpenaiGrayscale,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    // 非MadHouse，所有团队角色可访问
    notMh: getAccess({ condition: notMh, roleRestrictions: [ROLE_PERMISSIONS.ALL_VIEW] }),
    // 钱包，团队用户不可访问
    hasWallet: getAccess({
      condition: !!currentUser?.walletInfo?.walletId,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    // 额度，团队用户不可访问
    hasCredit: getAccess({
      condition: !!currentUser?.creditCard?.pkgId,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    // 客户消费配置，团队用户不可访问
    customerConsumptionConfigured: getAccess({
      condition: !!currentUser?.customerConsumptionConfigured,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    // 广告指引，团队用户不可访问
    isAdvertisingGuidance: getAccess({
      condition: !!currentUser?.guidanceExist,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    // 防盗刷监控，Admin可访问，主管理员与子管理员可见，团队用户不可访问
    customerBrushMonitoring: getAccess({
      condition: !!isBrushMonitoring,
      roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER],
    }),
    // // 监控预警，团队用户不可访问,子管理员不可访问
    monitoring: getAccess({ condition: true, roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER] }),
    // 监控预警父菜单，主管理员与子管理员可见，团队用户不可访问
    monitoringMenu: getAccess({
      condition: true,
      roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER],
    }),
    // 旧人员管理，仅Admin可访问，其他团队角色不可访问
    personnelManage: getAccess({
      condition: groupInfo ? groupInfo.some((g) => g.name === ROLE.ADMIN) : false,
      roleRestrictions: [ROLE_PERMISSIONS.NOT_VIEW],
    }),
    // 新人员管理，仅开通团队管理灰度，仅主管理员可访问
    personnelManageNew: getAccess({
      condition: !!teamManage,
      roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER],
    }),
    // 团队管理灰度，团队用户不可访问
    teamManageMenu: getAccess({
      condition: !!teamManage,
      roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER],
    }),
    // 非预付实销，所有团队 Manager、Customer、TeamUser 可访问
    isNonPrepayConsume: getAccess({
      condition: isNonPrepayConsume,
      roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER_OR_TEAM_USER],
    }),
    // 非预付实销 TikTok，所有团队 Manager、Customer、TeamUser 可访问
    isNonPrepayConsumeTiktok: getAccess({
      condition: isNonPrepayConsume,
      roleRestrictions: [ROLE_PERMISSIONS.IS_MANAGER_OR_CUSTOMER_OR_TEAM_USER],
    }),
    isNonPrepayConsumeCID: getAccess({
      condition: isNonPrepayConsume,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    isNonPrepayConsumeOtherMediaNeeds: getAccess({
      condition: isNonPrepayConsume,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    // 新兴媒体AI开户灰度
    isEmergingMediaAiAssist,
    // 封禁池/提交封禁预提 灰度（true 时展示提交封禁预提按钮）
    isDisabledPool,
    // OpenAI开户灰度（开户申请、开户列表 tab 是否展示 OpenAI）
    isOpenaiGrayscale,
    // TT风控大盘灰度（true 时 TikTok 媒体消耗质量 tab 才展示）
    isTtRiskVlDashboard,
  };

  // 菜单 和 角色 权限列表
  const menuAndRoleAccessList = {
    // 预付实销
    isPrepayConsume: getAccess({
      condition: isPrepayConsume,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
  };

  const roleAccessList = {
    // 非BV
    isBV: getAccess({ condition: isBV, roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT] }),
    // 除FB外其他媒体开户: 非MadHouse 且 非预付实销
    notMhAndNonPrepayConsume: getAccess({
      condition: notMh && !isPrepayConsume,
      roleRestrictions: [ROLE_PERMISSIONS.IS_TEAM_USER_NOT],
    }),
    isTomato: isTomato,
    canAdmin: currentUser && currentUser.access === 'admin',
    ...rolePermissions,
  };

  return {
    ...menuAccessList,
    ...menuAndRoleAccessList,
    ...roleAccessList,
  };
}
